// State 1 of the handoff (Active), plus the real states our backend
// actually has that the handoff's simplified 3-state model doesn't cover on
// its own — renewal_due — shown as a banner above the same
// hero rather than as separate pages, since they're the same subscription,
// just needing attention.
import React, { useState } from "react";
import { CalendarDays, ExternalLink, Info, Users } from "lucide-react";
import { toast } from "../../../utils/appToast";

import {
  formatDate,
  formatMoney,
  openRfpPaymentPortal,
  renewRfpSubscription,
  resumeRfpPlan,
  setRfpAutoRenew,
} from "../../../services/rfpBillingApi";
import ManageSeatsDialog from "./ManageSeatsDialog";
import CancelPlanDialog from "./CancelPlanDialog";
import AutoPayOffDialog from "./AutoPayOffDialog";
import BillingHistoryTable from "./BillingHistoryTable";
import ComparePlansTable from "./ComparePlansTable";
import PlanHero, { HERO_BTN, HeroBar, HeroFigure, HeroStats, NextCharge, PaymentMethodRow } from "./PlanHero";

export default function ActiveCard({ subscription, canManageBilling, onRefresh }) {
  const [dialog, setDialog] = useState(null); // null | "seats" | "cancel" | "autoOff"
  const [busy, setBusy] = useState(false);

  const {
    status,
    seat_quantity: seatQuantity,
    scheduled_seat_quantity: scheduledSeats,
    unit_amount: unitAmount,
    currency,
    auto_renew: autoRenew,
    cancel_at_period_end: cancelAtPeriodEnd,
    current_period_start: currentPeriodStart,
    current_period_end: currentPeriodEnd,
    created_at: createdAt,
    seats,
    payment_method: paymentMethod,
  } = subscription;

  const seatsUsed = seats?.used ?? 1;
  const total = unitAmount * seatQuantity;
  // A scheduled seat decrease takes effect at the next renewal, so that's the seat count the next payment is for.
  const nextSeats = scheduledSeats ?? seatQuantity;
  const origin = window.location.origin;

  const handleRenew = async () => {
    setBusy(true);
    try {
      const { checkout_url: checkoutUrl } = await renewRfpSubscription({
        successUrl: `${origin}/account/billing?billing=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/account/billing?billing=cancelled`,
      });
      if (checkoutUrl) window.location.href = checkoutUrl;
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't start renewal — try again");
      setBusy(false);
    }
  };

  const handleEnableAutoRenew = async () => {
    setBusy(true);
    try {
      const { checkout_url: checkoutUrl } = await setRfpAutoRenew({
        enabled: true,
        successUrl: `${origin}/account/billing?billing=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/account/billing?billing=cancelled`,
      });
      // No checkout when Stripe still has the subscription: it's simply switched back on.
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }
      toast.success("Auto-pay is back on");
      setBusy(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Couldn't enable auto-pay — try again");
      setBusy(false);
    }
  };

  const handleToggleAutoRenew = () => {
    if (!canManageBilling) return;
    if (autoRenew) {
      setDialog("autoOff");
      return;
    }
    handleEnableAutoRenew();
  };

  const handleOpenPortal = async () => {
    setBusy(true);
    try {
      const { portal_url: portalUrl } = await openRfpPaymentPortal(`${origin}/account/billing`);
      if (portalUrl) window.location.href = portalUrl;
    } catch {
      toast.error("Couldn't open Stripe — try again");
      setBusy(false);
    }
  };

  const [chipTone, chipLabel] = cancelAtPeriodEnd
    ? ["muted", `Ends ${formatDate(currentPeriodEnd)}`]
    : status === "renewal_due"
    ? ["attention", "Renewal due"]
    : ["ok", "Active"];

  return (
    <div className="flex flex-col gap-[22px]">
      {status === "renewal_due" && !cancelAtPeriodEnd && (
        <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[#E9D2E6] bg-[#FBF5FA] px-[18px] py-4">
          <div>
            <p className="text-[15px] font-semibold text-[#1A1A1A]">Your subscription expires soon</p>
            <p className="mt-0.5 text-[13.5px] text-[#52525B]">Renews on {formatDate(currentPeriodEnd)} — renew now to keep uninterrupted access.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleRenew}
              disabled={!canManageBilling || busy}
              className="rounded-[9px] border border-[#E9D2E6] px-4 py-2 text-[14px] font-medium text-[#893F82] disabled:opacity-50"
            >
              Renew manually
            </button>
            <button
              type="button"
              onClick={handleEnableAutoRenew}
              disabled={!canManageBilling || busy}
              className="rounded-[9px] bg-[#A04A97] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-50"
            >
              Renew & enable auto-pay
            </button>
          </div>
        </div>
      )}

      {cancelAtPeriodEnd && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-[#ECE9EE] bg-[#FAF9FB] px-[18px] py-4">
          <p className="text-[13.5px] text-[#52525B]">
            <Info size={14} className="mr-1.5 inline -mt-0.5" /> Your plan ends on{" "}
            <b className="text-[#1A1A1A]">{formatDate(currentPeriodEnd)}</b>.
          </p>
          <button
            type="button"
            onClick={async () => {
              setBusy(true);
              try {
                await resumeRfpPlan();
                toast.success("Plan resumed.");
                onRefresh?.();
              } catch (err) {
                toast.error(err?.response?.data?.error || "Couldn't resume — try again");
              } finally {
                setBusy(false);
              }
            }}
            disabled={!canManageBilling || busy}
            className="rounded-[9px] border border-[#ECE9EE] px-4 py-2 text-[14px] font-medium text-[#1A1A1A] disabled:opacity-50"
          >
            Resume plan
          </button>
        </div>
      )}

      <PlanHero
        tone={chipTone}
        label={chipLabel}
        aside={
          <>
            <NextCharge
              label="Next payment"
              amount={cancelAtPeriodEnd ? formatMoney(0, currency) : formatMoney(unitAmount * nextSeats, currency)}
              note={
                cancelAtPeriodEnd
                  ? "No upcoming charges"
                  : `on ${formatDate(currentPeriodEnd)}${nextSeats !== seatQuantity ? ` · ${nextSeats} user${nextSeats === 1 ? "" : "s"}` : ""}`
              }
            />

            <PaymentMethodRow
              paymentMethod={paymentMethod}
              onUpdate={handleOpenPortal}
              disabled={!canManageBilling || busy}
              busy={busy}
            />

            <div className="flex items-center justify-between rounded-xl border border-[#ECE9EE] p-3">
              <div>
                <p className="text-[13.5px] font-medium text-[#1A1A1A]">Auto-pay</p>
                <p className="text-[12px] text-[#8A8A90]">Charges your card automatically each cycle</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={autoRenew}
                onClick={handleToggleAutoRenew}
                disabled={!canManageBilling || cancelAtPeriodEnd}
                className="relative h-[22px] w-[38px] shrink-0 rounded-full transition disabled:opacity-40"
                style={{ background: autoRenew ? "#A04A97" : "#D4D4D8" }}
              >
                <span
                  className="absolute top-[3px] h-4 w-4 rounded-full bg-white transition-all"
                  style={{ left: autoRenew ? "19px" : "3px" }}
                />
              </button>
            </div>

            <div className="flex items-center justify-between border-t border-[#ECE9EE] pt-3">
              <button
                type="button"
                onClick={handleOpenPortal}
                disabled={!canManageBilling || busy}
                className="flex items-center gap-1.5 text-[13px] font-medium text-[#893F82] disabled:opacity-50"
              >
                Billing portal <ExternalLink size={13} />
              </button>
              <span className="flex items-center gap-1 text-[11.5px] text-[#8A8A90]">
                <CalendarDays size={12} /> Billed in USD
              </span>
            </div>
          </>
        }
      >
        <div>
          <h3 className="text-[28px] font-semibold leading-tight">Premium</h3>
          <p className="text-[14px] text-white/72">RFP Manager</p>
        </div>

        <HeroFigure
          value={formatMoney(total, currency)}
          caption={`/ month · ${formatMoney(unitAmount, currency)} × ${seatQuantity} users`}
        />

        <HeroStats
          stats={[
            ["Member since", formatDate(createdAt)],
            ["Billing cycle", "Monthly"],
            ["Renews", cancelAtPeriodEnd ? "—" : formatDate(currentPeriodEnd)],
          ]}
        />

        <div>
          <div className="mb-1.5 flex items-center justify-between text-[13.5px]">
            <span className="flex items-center gap-1.5 text-white/80">
              <Users size={14} /> Seats assigned
            </span>
            <span className="font-semibold [font-variant-numeric:tabular-nums]">
              {seatsUsed} of {seatQuantity}
            </span>
          </div>
          <HeroBar pct={(seatsUsed / seatQuantity) * 100} />
        </div>

        {!cancelAtPeriodEnd && (
          <div className="mt-1 flex items-center gap-4">
            <button
              type="button"
              onClick={() => setDialog("seats")}
              disabled={!canManageBilling}
              className={HERO_BTN.primary}
            >
              Manage seats
            </button>
            {canManageBilling && (
              <button type="button" onClick={() => setDialog("cancel")} className={HERO_BTN.link}>
                Cancel plan
              </button>
            )}
          </div>
        )}
      </PlanHero>

      {canManageBilling && <BillingHistoryTable />}

      <ComparePlansTable />

      <ManageSeatsDialog
        open={dialog === "seats"}
        onClose={() => setDialog(null)}
        currentSeats={seatQuantity}
        seatsUsed={seatsUsed}
        unitAmount={unitAmount}
        periodStart={currentPeriodStart}
        periodEnd={currentPeriodEnd}
        onChanged={onRefresh}
      />
      <CancelPlanDialog
        open={dialog === "cancel"}
        onClose={() => setDialog(null)}
        periodEndLabel={formatDate(currentPeriodEnd)}
        onCancelled={onRefresh}
      />
      <AutoPayOffDialog open={dialog === "autoOff"} onClose={() => setDialog(null)} onChanged={onRefresh} />
    </div>
  );
}
